import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { X, Plus, Tag, Loader2, Check, Trash2 } from 'lucide-react';

const BulkTagManager = ({ 
  isOpen, 
  onClose, 
  entityType = 'Customer', 
  entityIds = [], 
  entityCount = 0,
  onSuccess 
}) => {
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tagsToAdd, setTagsToAdd] = useState([]);
  const [tagsToRemove, setTagsToRemove] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [showNewTagInput, setShowNewTagInput] = useState(false);

  const fetchAllTags = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch tags filtered by entity type to show only relevant tags
      const url = entityType ? `/api/tags?entityType=${entityType}` : '/api/tags';
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tags');
      }

      const data = await response.json();
      setAllTags(data.tags || []);
    } catch (error) {
      console.error('Failed to fetch tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    if (isOpen) {
      fetchAllTags();
    }
  }, [isOpen, fetchAllTags]);

  const handleAddNewTag = () => {
    if (!newTagName.trim()) {
      toast.error('Please enter a tag name');
      return;
    }

    const tagName = newTagName.trim();
    
    // Check if tag already exists
    if (tagsToAdd.includes(tagName)) {
      toast.error('Tag is already in the add list');
      return;
    }

    // Add to the add list
    setTagsToAdd([...tagsToAdd, tagName]);
    
    // Remove from remove list if present
    setTagsToRemove(tagsToRemove.filter(t => t !== tagName));
    
    setNewTagName('');
    setShowNewTagInput(false);
    toast.success(`Added "${tagName}" to apply list`);
  };

  const handleToggleAddTag = (tagName) => {
    if (tagsToAdd.includes(tagName)) {
      // Remove from add list
      setTagsToAdd(tagsToAdd.filter(t => t !== tagName));
    } else {
      // Add to add list
      setTagsToAdd([...tagsToAdd, tagName]);
      // Remove from remove list if present
      setTagsToRemove(tagsToRemove.filter(t => t !== tagName));
    }
  };

  const handleToggleRemoveTag = (tagName) => {
    if (tagsToRemove.includes(tagName)) {
      // Remove from remove list
      setTagsToRemove(tagsToRemove.filter(t => t !== tagName));
    } else {
      // Add to remove list
      setTagsToRemove([...tagsToRemove, tagName]);
      // Remove from add list if present
      setTagsToAdd(tagsToAdd.filter(t => t !== tagName));
    }
  };

  const handleApply = async () => {
    if (tagsToAdd.length === 0 && tagsToRemove.length === 0) {
      toast.error('Please select at least one tag to add or remove');
      return;
    }

    try {
      setSaving(true);

      const response = await fetch('/api/tags/bulk-apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          entityType,
          entityIds,
          tagsToAdd: tagsToAdd.length > 0 ? tagsToAdd : undefined,
          tagsToRemove: tagsToRemove.length > 0 ? tagsToRemove : undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to apply tags');
      }

      const result = await response.json();
      
      toast.success(
        `Tags applied successfully! Added: ${result.addedCount}, Removed: ${result.removedCount}`
      );

      // Reset state
      setTagsToAdd([]);
      setTagsToRemove([]);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(result);
      }

      // Close modal
      onClose();
    } catch (error) {
      console.error('Failed to apply tags:', error);
      toast.error(error.message || 'Failed to apply tags');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setTagsToAdd([]);
      setTagsToRemove([]);
      setNewTagName('');
      setShowNewTagInput(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Manage Tags
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Apply tags to {entityCount} selected {entityType.toLowerCase()}{entityCount !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={handleClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Tags to Add Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-900 dark:text-white">
                    Tags to Add
                  </label>
                  <button
                    onClick={() => setShowNewTagInput(!showNewTagInput)}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center space-x-1"
                  >
                    <Plus className="h-3 w-3" />
                    <span>Create New</span>
                  </button>
                </div>

                {showNewTagInput && (
                  <div className="flex space-x-2 mb-3">
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNewTag()}
                      placeholder="Enter new tag name..."
                      className="flex-1 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      autoFocus
                    />
                    <button
                      onClick={handleAddNewTag}
                      className="px-3 py-2 bg-blue-600 dark:bg-blue-500 text-white rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 text-sm"
                    >
                      Add
                    </button>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 min-h-[60px] p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  {tagsToAdd.length === 0 ? (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Select tags below to add to selected {entityType.toLowerCase()}s
                    </span>
                  ) : (
                    tagsToAdd.map((tagName) => (
                      <span
                        key={tagName}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        {tagName}
                        <button
                          onClick={() => handleToggleAddTag(tagName)}
                          className="ml-2 hover:text-green-900 dark:hover:text-green-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>

              {/* Tags to Remove Section */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Tags to Remove
                </label>
                <div className="flex flex-wrap gap-2 min-h-[60px] p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                  {tagsToRemove.length === 0 ? (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Select tags below to remove from selected {entityType.toLowerCase()}s
                    </span>
                  ) : (
                    tagsToRemove.map((tagName) => (
                      <span
                        key={tagName}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        {tagName}
                        <button
                          onClick={() => handleToggleRemoveTag(tagName)}
                          className="ml-2 hover:text-red-900 dark:hover:text-red-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>

              {/* Available Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Available Tags
                </label>
                <div className="flex flex-wrap gap-2">
                  {allTags.length === 0 ? (
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      No tags available. Create a new tag above.
                    </span>
                  ) : (
                    allTags.map((tag) => {
                      const isAdding = tagsToAdd.includes(tag.name);
                      const isRemoving = tagsToRemove.includes(tag.name);
                      
                      return (
                        <div key={tag.id} className="flex space-x-1">
                          <button
                            onClick={() => handleToggleAddTag(tag.name)}
                            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              isAdding
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-2 border-green-500 dark:border-green-400'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
                            }`}
                            title="Click to add tag"
                          >
                            <Tag className="h-3 w-3 mr-1" />
                            {tag.name}
                            {tag.usageCount > 0 && (
                              <span className="ml-2 text-xs opacity-60">
                                ({tag.usageCount})
                              </span>
                            )}
                          </button>
                          <button
                            onClick={() => handleToggleRemoveTag(tag.name)}
                            className={`p-1.5 rounded-lg text-sm transition-colors ${
                              isRemoving
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-2 border-red-500 dark:border-red-400'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 border border-gray-300 dark:border-gray-600 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400'
                            }`}
                            title="Click to remove tag"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={saving || (tagsToAdd.length === 0 && tagsToRemove.length === 0)}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>Apply Tags</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkTagManager;

